import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Position config — extracted for easy tweaking
// Clock sits to the right of the home button on the left side of the toolbar
const CLOCK_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      fontMultiplier: 1.1,
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.35,
      y: (toolbar, buttonConfig) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      fontMultiplier: 1.0,
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.55,
      y: (toolbar, buttonConfig) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      fontMultiplier: 1.55,
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.4,
      y: (toolbar, buttonConfig) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      fontMultiplier: 1.1,
      x: (toolbar, buttonConfig) => buttonConfig.width * 1.5,
      y: (toolbar, buttonConfig) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    fontMultiplier: 0.8,
    x: (toolbar, buttonConfig) => buttonConfig.width * 1.7,
    y: (toolbar, buttonConfig) => toolbar.height / 2,
  },
};

const Clock = ({ timeZone = "Europe/Belgrade", toolbar }) => {
  const { device, orientation, getResponsiveConfig, uiSymbolScale } =
    useDimensions();
  const [time, setTime] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(dayjs());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format the time string
  const formattedTime = useMemo(
    () => time.tz(timeZone).format("HH:mm"),
    [time, timeZone],
  );

  // Get button dimensions
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Clock position — left side, next to home button
  const clockConfig = useMemo(() => {
    const deviceConfig = CLOCK_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      fontMultiplier: posConfig.fontMultiplier,
      x: posConfig.x(toolbar, buttonConfig),
      y: posConfig.y(toolbar, buttonConfig),
    };
  }, [device, orientation, toolbar, buttonConfig]);

  // Calculate font size
  const baseFontSize = 28;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * clockConfig.fontMultiplier;
    return Math.round(responsiveFontSize * uiSymbolScale);
  }, [clockConfig.fontMultiplier, uiSymbolScale]);

  // Text style
  const clockStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "500",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  // Rounded position
  const clockPosition = useMemo(
    () => ({
      x: Math.round(clockConfig.x),
      y: Math.round(clockConfig.y),
    }),
    [clockConfig.x, clockConfig.y],
  );

  return (
    <Container
      name="backport_clock"
      x={clockPosition.x}
      y={clockPosition.y}
      zIndex={3}
    >
      <Text
        text={formattedTime}
        style={clockStyle}
        anchor={0.5}
        x={0}
        y={0}
        resolution={2}
        accessible={true}
        accessibleTitle={formattedTime}
      />
    </Container>
  );
};

export default Clock;
